import React, { useEffect } from 'react';
import {
  MessageCircle,
  ArrowRight,
  Calendar,
  Clock,
  BookOpen
} from 'lucide-react';

const Blog = () => {
  useEffect(() => {
    // Google Analytics
    window.dataLayer = window.dataLayer || [];
    function gtag(){window.dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', 'G-V8ESDMZ5NZ');

    // SEO-OPTIMIZED: Title and meta description
    document.title = 'Customer Support Blog | TicketDesk';

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = 'Learn customer support best practices, multi-channel ticketing strategies, review management tips, and help desk optimization guides for small businesses.';

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = 'https://www.ticketdesk.app/blog';

    // Structured data for blog
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Blog",
      "name": "TicketDesk Blog",
      "description": "Customer support tips, help desk strategies, and ticketing best practices for small businesses",
      "publisher": {
        "@type": "Organization",
        "name": "TicketDesk",
        "logo": {
          "@type": "ImageObject",
          "url": "https://ticketdesk.app/logo.png"
        }
      }
    };

    let script = document.querySelector('script[type="application/ld+json"]');
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(structuredData);
  }, []);

  const trackEvent = (eventName, eventParams = {}) => {
    if (window.gtag) {
      window.gtag('event', eventName, eventParams);
    }
  };

  const blogPosts = [
    {
      title: 'Best Zendesk Alternatives for Small Businesses (2025)',
      excerpt: 'Tired of Zendesk\'s high prices and complexity? Discover affordable, easy-to-use customer support software built for small businesses. Compare features, pricing, and user reviews.',
      readTime: '12 min read',
      date: 'January 15, 2025',
      slug: '/blog/zendesk-alternatives-small-business',
      category: 'Comparisons'
    },
    {
      title: 'How to Handle Customer Support Across Multiple Channels',
      excerpt: 'Managing customer inquiries from Gmail, Facebook, WhatsApp, and your website? Learn how to unify multi-channel support without losing your mind or dropping messages.',
      readTime: '10 min read',
      date: 'January 12, 2025',
      slug: '/blog/customer-support-multiple-channels',
      category: 'Best Practices'
    },
    {
      title: 'How to Respond to Negative Reviews Fast (Before They Hurt Sales)',
      excerpt: 'Negative reviews on Trustpilot, Google, or Capterra can kill sales if ignored. Learn proven strategies to respond quickly, turn unhappy customers around, and protect your reputation.',
      readTime: '8 min read',
      date: 'January 10, 2025',
      slug: '/blog/respond-negative-reviews-fast',
      category: 'Reputation Management'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <a href="/" className="flex items-center min-w-0">
              <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-message-circle">
                  <path d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719"></path>
                </svg>
              </div>
              <span className="ml-2 sm:ml-3 text-lg sm:text-xl font-semibold text-gray-900">TicketDesk</span>
            </a>
            <div className="flex items-center space-x-2 sm:space-x-4 lg:space-x-8">
              <a href="/blog" className="text-xs sm:text-sm text-purple-600 font-medium hidden md:block">Blog</a>
              <a href="/#features" className="text-xs sm:text-sm text-gray-700 hover:text-gray-900 hidden md:block">Features</a>
              <a href="/#pricing" className="text-xs sm:text-sm text-gray-700 hover:text-gray-900">Pricing</a>
              <a href="/login" className="text-xs sm:text-sm text-gray-700 hover:text-gray-900 hidden lg:block">Sign In</a>
              <a href="/register" className="bg-purple-600 text-white px-3 sm:px-4 lg:px-5 py-1.5 sm:py-2 rounded text-xs sm:text-sm font-medium hover:bg-purple-700 whitespace-nowrap">
                Try free
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-purple-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <BookOpen className="w-4 h-4 mr-2" />
              Customer Support Blog
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
              Learn How to Master Customer Support
            </h1>

            <p className="text-base sm:text-lg lg:text-xl text-gray-600 mb-6 leading-relaxed">
              Practical guides, best practices, and strategies for handling customer support like a pro. Perfect for small businesses and growing teams.
            </p>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post, index) => (
              <article
                key={index}
                className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-lg transition-shadow duration-300 overflow-hidden group"
              >
                <div className="p-6">
                  <div className="mb-4">
                    <span className="inline-block bg-purple-100 text-purple-700 text-xs font-semibold px-3 py-1 rounded-full">
                      {post.category}
                    </span>
                  </div>

                  <h2 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-purple-600 transition-colors">
                    {post.title}
                  </h2>

                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>

                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      {post.date}
                    </div>
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      {post.readTime}
                    </div>
                  </div>

                  <a
                    href={post.slug}
                    onClick={() => trackEvent('blog_post_click', { post_title: post.title })}
                    className="inline-flex items-center text-purple-600 font-semibold hover:text-purple-700 transition-colors"
                  >
                    Read Article
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-20 bg-purple-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Ready to Transform Your Customer Support?
          </h2>
          <p className="text-xl text-white mb-8 opacity-90">
            Put these strategies into action with TicketDesk. Start your free trial today.
          </p>
          <a
            href="/register"
            onClick={() => trackEvent('cta_click', { location: 'blog_index' })}
            className="inline-flex items-center justify-center bg-white text-purple-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100"
          >
            Start Free Trial
            <ArrowRight className="w-5 h-5 ml-2" />
          </a>
          <p className="text-white mt-4 opacity-75 text-sm">No credit card required • Free plan available</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <div className="col-span-2">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719"></path>
                  </svg>
                </div>
                <span className="ml-3 text-xl font-bold">TicketDesk</span>
              </div>
              <p className="text-gray-400 max-w-md">
                Customer support software for small businesses. Unify Gmail, Facebook, WhatsApp, and review monitoring in one simple inbox.
              </p>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Product</h3>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li><a href="/#features" className="hover:text-white">Features</a></li>
                <li><a href="/#pricing" className="hover:text-white">Pricing</a></li>
                <li><a href="/integrations" className="hover:text-white">Integrations</a></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Resources</h3>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li><a href="/blog" className="hover:text-white">Blog</a></li>
                <li><a href="/compare/zendesk" className="hover:text-white">vs Zendesk</a></li>
                <li><a href="/for-ecommerce" className="hover:text-white">For E-commerce</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; 2025 TicketDesk. Customer support software for small businesses.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Blog;
